import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMonthlyProcessRun } from './hooks/useMonthlyProcessRun';
import AppDropdown from '../../../components/AppDropdown';

export default function MonthlyProcessRunScreen() {
  const { state, actions } = useMonthlyProcessRun();
  const navigate = useNavigate();
  const { companyId, branchId, deptId, month, year, isProcessing } = state;

  const monthLabel = state.monthOptions.find((m) => m.value === month)?.label || month;

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Monthly Attendance Process
          </h1>
          <p className="text-xs text-on-surface-variant font-medium mt-1">
            Month — {monthLabel} {year}
          </p>
        </div>

        <Link
          to="/payroll/monthly-process"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">
              Configure Processing Parameters
            </h2>
          </div>

          {/* Form Body */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-5">

              {/* Company */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Company
                </label>
                <AppDropdown
                  options={state.companyOptions}
                  value={companyId}
                  onChange={actions.setCompanyId}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Branch */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Branch
                </label>
                <AppDropdown
                  options={state.branchOptions}
                  value={branchId}
                  onChange={actions.setBranchId}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Department */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Department
                </label>
                <AppDropdown
                  options={state.departmentOptions}
                  value={deptId}
                  onChange={actions.setDeptId}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Month */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Month <span className="text-rose-500">*</span>
                </label>
                <AppDropdown
                  options={state.monthOptions}
                  value={month}
                  onChange={actions.setMonth}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Year */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Year <span className="text-rose-500">*</span>
                </label>
                <AppDropdown
                  options={state.yearOptions}
                  value={year}
                  onChange={actions.setYear}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isProcessing}
              onClick={actions.handleProcess}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">event_repeat</span>
              <span>{isProcessing ? 'Processing...' : 'Process'}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/payroll/monthly-process')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Context Panel */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500 font-bold">lightbulb</span>
            <span>How It Works:</span>
          </div>

          <div className="space-y-3 text-xs text-on-surface-variant leading-relaxed font-medium">
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined text-sm text-primary mt-0.5 shrink-0">looks_one</span>
              <p><strong className="text-on-surface">Sandwich Rule:</strong> Automatically marks WO/Holiday as Absent when flanked by absent days and rule is not opted out.</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined text-sm text-primary mt-0.5 shrink-0">looks_two</span>
              <p><strong className="text-on-surface">Aggregation:</strong> Consolidates daily EmpAttendance — counts Absent, Half Day (0.5), Late &amp; Early Go penalties from attendance rules.</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined text-sm text-primary mt-0.5 shrink-0">looks_3</span>
              <p><strong className="text-on-surface">Formula:</strong> TotalPresent = TotalDays − (Absent + HalfDay + Late Penalty + EarlyGo Penalty).</p>
            </div>
          </div>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">Monthly Audit Rules:</h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Idempotent Check:</strong> If attendance is already processed for the selected Month/Year/Company/Branch/Dept, the process will be blocked.
              </li>
              <li>
                <strong className="text-on-surface">Salary Readiness:</strong> Completed monthly process locks the attendance period for one-click Salary Process generation.
              </li>
              <li>
                <strong className="text-on-surface">Rollback Protection:</strong> Rollback is blocked if salary has already been processed for that month.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
